// Renders a professional, printable platform invoice in a new window and
// triggers the browser's print dialog. The user can choose Print or
// Save-as-PDF — same artifact either way.
//
// Used by both Platform Admin (PlatformAdmin.vue) and the school-side
// MyPlatformInvoices.vue. Takes the payload from getPlatformInvoiceDetails.

const SIMI_LOGO = 'https://api.paysol.africa/simitrack/uploads/logos/simi_trac_logo.png?v=20260628';
const SIMI_BRAND = {
  name: 'Simi Trac Limited',
  tagline: 'School Operations Platform',
  address: 'Nairobi, Kenya',
  email: 'billing@simitrac.co.ke',
  phone: '+254 700 000 000',
  website: 'simitrac.co.ke',
};

function fmt(n) {
  return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function dt(s) { return s ? String(s).substr(0, 10) : '—'; }
function escape(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c]);
}

const SOURCE_LABELS = {
  pos_commission: 'POS commission',
  card_fee: 'Student card issuance / renewal',
  attendance_parent: 'Per-student platform fee (parent-paid)',
  attendance_school: 'Per-student platform fee (school-paid)',
};

export function printPlatformInvoice(payload) {
  if (!payload || !payload.invoice) {
    alert('Could not load invoice details.');
    return;
  }

  const inv = payload.invoice;
  const inst = payload.institution || {};
  const paybill = payload.platform_paybill || null;
  const breakdown = payload.breakdown || [];
  const outstanding = Number(payload.outstanding || 0);
  const paidAmount = Number(payload.paid_amount || 0);
  const status = (payload.status || 'pending').toLowerCase();
  const isVoid = !!inv.voided_at;

  const statusBadge = isVoid ? { label: 'VOID', color: '#EF4444' }
    : status === 'paid'    ? { label: 'PAID', color: '#10B981' }
    : status === 'partial' ? { label: 'PARTIAL', color: '#F59E0B' }
    :                        { label: 'UNPAID', color: '#F59E0B' };

  const breakdownRows = breakdown.map(b => {
    const label = SOURCE_LABELS[b.source_type] || b.source_type;
    const desc = b.source_type === 'pos_commission'
      ? `${b.line_count} transaction${b.line_count == 1 ? '' : 's'} · KSh ${fmt(b.gross)} gross POS sales`
      : b.source_type === 'card_fee'
      ? `${b.line_count} card${b.line_count == 1 ? '' : 's'} (issuance / renewal)`
      : `${b.line_count} line${b.line_count == 1 ? '' : 's'}`;
    return `
      <tr>
        <td style="padding:10px 12px;border-bottom:1px solid #E5E7EB;">
          <div style="font-weight:600;color:#0D2B5E;">${escape(label)}</div>
          <div style="font-size:11px;color:#6B7280;margin-top:2px;">${escape(desc)}</div>
        </td>
        <td style="padding:10px 12px;border-bottom:1px solid #E5E7EB;text-align:right;color:#374151;">KSh ${fmt(b.gross)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #E5E7EB;text-align:right;color:#9CA3AF;">${Number(b.prepaid) > 0.01 ? '− KSh ' + fmt(b.prepaid) : '—'}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #E5E7EB;text-align:right;font-weight:700;color:#0D2B5E;">KSh ${fmt(b.net)}</td>
      </tr>
    `;
  }).join('');

  const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8" />
<title>${escape(inv.invoice_no)} — ${escape(inst.name || '')}</title>
<style>
@page { size: A4; margin: 18mm; }
* { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #1F2937; font-size: 13px; line-height: 1.5; margin: 0; padding: 0; background: #fff; }
.sheet { max-width: 190mm; margin: 0 auto; padding: 8mm 0; }

/* Header */
.hdr { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 3px solid #0D2B5E; }
.hdr-left img { height: 44px; display: block; margin-bottom: 8px; }
.hdr-brand { font-size: 18px; font-weight: 800; color: #0D2B5E; letter-spacing: 0.3px; }
.hdr-tag { font-size: 11px; color: #6B7280; }
.hdr-right { text-align: right; }
.hdr-title { font-size: 28px; font-weight: 800; color: #0D2B5E; letter-spacing: 4px; margin-bottom: 4px; }
.hdr-no { font-family: monospace; font-size: 14px; color: #374151; font-weight: 700; }
.hdr-status { display: inline-block; margin-top: 6px; padding: 4px 14px; border-radius: 50px; font-size: 11px; font-weight: 800; letter-spacing: 1px; color: #fff; }

/* Two-column meta */
.meta-grid { display: flex; gap: 24px; margin-bottom: 28px; }
.meta-col { flex: 1; }
.meta-lbl { font-size: 10px; font-weight: 700; color: #6B7280; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 6px; }
.meta-name { font-size: 15px; font-weight: 700; color: #0D2B5E; margin-bottom: 4px; }
.meta-line { font-size: 12px; color: #4B5563; line-height: 1.6; }
.meta-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.meta-table td { padding: 4px 0; }
.meta-table td:first-child { color: #6B7280; }
.meta-table td:last-child { color: #1F2937; font-weight: 600; text-align: right; }

/* Items */
.items { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
.items thead th { background: #F3F4F6; padding: 10px 12px; font-size: 11px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; text-align: left; border-bottom: 2px solid #D1D5DB; }
.items thead th.r { text-align: right; }

/* Totals */
.totals { display: flex; justify-content: flex-end; margin-bottom: 28px; }
.totals-table { min-width: 280px; }
.totals-table td { padding: 6px 12px; font-size: 13px; }
.totals-table td:first-child { color: #6B7280; text-align: right; }
.totals-table td:last-child { font-weight: 700; color: #1F2937; text-align: right; }
.totals-grand { background: #0D2B5E; color: #fff !important; }
.totals-grand td { color: #fff !important; padding: 12px; font-size: 16px; font-weight: 800; }

/* Payment instructions */
.pay-card { background: #ECFDF5; border-left: 4px solid #10B981; border-radius: 4px; padding: 14px 18px; margin-bottom: 24px; }
.pay-card.unpaid { background: #FFFBEB; border-left-color: #F59E0B; }
.pay-card-title { font-size: 12px; font-weight: 800; color: #065F46; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 8px; }
.pay-card.unpaid .pay-card-title { color: #92400E; }
.pay-line { font-size: 13px; color: #065F46; line-height: 1.7; }
.pay-card.unpaid .pay-line { color: #92400E; }
.pay-strong { font-weight: 800; font-family: monospace; background: #fff; padding: 1px 8px; border-radius: 3px; }

/* Payments history */
.payments { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
.payments thead th { background: #F9FAFB; padding: 8px 12px; font-size: 10px; font-weight: 700; color: #6B7280; text-transform: uppercase; text-align: left; border-bottom: 1px solid #E5E7EB; }
.payments thead th.r { text-align: right; }
.section-title { font-size: 13px; font-weight: 800; color: #0D2B5E; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #E5E7EB; }

/* Footer */
.footer { margin-top: 36px; padding-top: 14px; border-top: 1px solid #E5E7EB; font-size: 11px; color: #9CA3AF; text-align: center; line-height: 1.6; }

/* Void watermark */
.void-stamp { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-25deg); font-size: 120px; font-weight: 900; color: rgba(239, 68, 68, 0.15); border: 8px solid rgba(239, 68, 68, 0.15); padding: 10px 40px; letter-spacing: 8px; pointer-events: none; z-index: 999; }

@media print {
  body { -webkit-print-color-adjust: exact; }
  .no-print { display: none !important; }
}
</style>
</head>
<body>
${isVoid ? '<div class="void-stamp">VOID</div>' : ''}
<div class="sheet">

  <!-- Header -->
  <div class="hdr">
    <div class="hdr-left">
      <img src="${SIMI_LOGO}" alt="Simi Trac" />
      <div class="hdr-brand">${escape(SIMI_BRAND.name)}</div>
      <div class="hdr-tag">${escape(SIMI_BRAND.tagline)}</div>
    </div>
    <div class="hdr-right">
      <div class="hdr-title">INVOICE</div>
      <div class="hdr-no">${escape(inv.invoice_no)}</div>
      <div class="hdr-status" style="background:${statusBadge.color};">${statusBadge.label}</div>
    </div>
  </div>

  <!-- Bill From / Bill To / Invoice meta -->
  <div class="meta-grid">
    <div class="meta-col">
      <div class="meta-lbl">FROM</div>
      <div class="meta-name">${escape(SIMI_BRAND.name)}</div>
      <div class="meta-line">${escape(SIMI_BRAND.address)}</div>
      <div class="meta-line">${escape(SIMI_BRAND.phone)}</div>
      <div class="meta-line">${escape(SIMI_BRAND.email)}</div>
      <div class="meta-line">${escape(SIMI_BRAND.website)}</div>
    </div>
    <div class="meta-col">
      <div class="meta-lbl">BILLED TO</div>
      <div class="meta-name">${escape(inst.name || '—')}</div>
      ${inst.type ? `<div class="meta-line" style="text-transform:capitalize;">${escape(inst.type)}</div>` : ''}
      ${inst.city ? `<div class="meta-line">${escape(inst.city)}</div>` : ''}
      ${inst.phone ? `<div class="meta-line">${escape(inst.phone)}</div>` : ''}
      ${inst.email ? `<div class="meta-line">${escape(inst.email)}</div>` : ''}
    </div>
    <div class="meta-col">
      <div class="meta-lbl">INVOICE DETAILS</div>
      <table class="meta-table">
        <tr><td>Issue date</td><td>${escape(dt(inv.issued_at))}</td></tr>
        <tr><td>Service period</td><td>${escape(dt(inv.period_start))} → ${escape(dt(inv.period_end))}</td></tr>
        <tr><td>Lines</td><td>${escape(inv.line_count)}</td></tr>
        ${inv.issued_by_name ? `<tr><td>Issued by</td><td>${escape(inv.issued_by_name)}</td></tr>` : ''}
      </table>
    </div>
  </div>

  <!-- Line items -->
  <table class="items">
    <thead><tr>
      <th>Description</th>
      <th class="r">Gross</th>
      <th class="r">Prepaid</th>
      <th class="r">Amount</th>
    </tr></thead>
    <tbody>${breakdownRows || '<tr><td colspan="4" style="padding:24px;text-align:center;color:#9CA3AF;">No line items</td></tr>'}</tbody>
  </table>

  <!-- Totals -->
  <div class="totals">
    <table class="totals-table">
      <tr><td>Invoice amount</td><td>KSh ${fmt(inv.amount)}</td></tr>
      ${paidAmount > 0.005 ? `<tr><td>Paid against invoice</td><td style="color:#10B981;">− KSh ${fmt(paidAmount)}</td></tr>` : ''}
      <tr class="totals-grand"><td>${outstanding > 0.005 ? 'BALANCE DUE' : 'TOTAL PAID'}</td><td>KSh ${fmt(outstanding > 0.005 ? outstanding : inv.amount)}</td></tr>
    </table>
  </div>

  ${outstanding > 0.005 && !isVoid && paybill ? `
  <!-- Payment instructions — only when there's a balance and a paybill is configured -->
  <div class="pay-card unpaid">
    <div class="pay-card-title">💳 How to pay</div>
    <div class="pay-line">
      Open M-Pesa → Lipa na M-Pesa → <strong>${paybill.type === 'till' ? 'Buy Goods &amp; Services' : 'Pay Bill'}</strong><br/>
      ${paybill.type === 'till' ? 'Till Number' : 'Business Number'}: <span class="pay-strong">${escape(paybill.shortcode)}</span><br/>
      ${paybill.type === 'paybill' ? `Account: <span class="pay-strong">${escape(inv.invoice_no)}</span><br/>` : ''}
      Amount: <span class="pay-strong">KSh ${fmt(outstanding)}</span>
    </div>
    <div class="pay-line" style="margin-top:8px;font-size:11px;opacity:.85;">
      Payment auto-matches when the M-Pesa receipt lands. You can also pay via the parent portal at simitrac.co.ke.
    </div>
  </div>
  ` : ''}

  ${inv.notes ? `
  <div class="section-title">Notes</div>
  <div style="font-size:12px;color:#4B5563;line-height:1.7;margin-bottom:16px;">${escape(inv.notes)}</div>
  ` : ''}

  <!-- Footer -->
  <div class="footer">
    Thank you for using ${escape(SIMI_BRAND.name)}. This invoice was generated automatically by the Simi Trac platform.<br/>
    Questions? Reply to ${escape(SIMI_BRAND.email)} quoting the invoice number above.
  </div>
</div>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) { alert('Pop-up blocked. Allow pop-ups for this site to print invoices.'); return; }
  w.document.write(html);
  w.document.close();
  // Brief pause so the logo image has a chance to load before the print dialog
  setTimeout(() => {
    try { w.document.title = inv.invoice_no + '_' + (inst.slug || inst.name || '').replace(/\s+/g,'_'); } catch(e) { /* doc title is cosmetic — ignore */ }
    w.print();
  }, 500);
}
